/**
 * Policy hygiene panel — CLARID-SPEC-BSA-001 §3.1.
 *
 * Renders Rusty Cronan's 14-item administrative checklist against one policy.
 *
 * The design job here is making the **determinism boundary** visible. A
 * compliance officer taking this into an examination needs to know, per line,
 * whether a result came from a database column or from a language model — and
 * for the model-derived ones, to read the exact sentence it relied on. So:
 *
 *   - every AI-assisted row is labelled and shows its confidence
 *   - every document-body PASS renders its verbatim excerpt, quoted
 *   - NEEDS_REVIEW is styled as a distinct state, not a soft fail — it means
 *     "a human must look", which is different from "the policy is deficient"
 *   - NOT_APPLICABLE always shows why it was skipped
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  MinusCircleIcon,
  SparklesIcon,
  CircleStackIcon,
} from '@heroicons/react/24/outline';
import { policyApi } from '../../lib/api';

type HygieneResult = 'PASS' | 'FAIL' | 'NOT_APPLICABLE' | 'NEEDS_REVIEW';

interface HygieneCheck {
  id: string;
  ruleKey: string;
  ruleText: string;
  result: HygieneResult;
  evidenceExcerpt: string | null;
  evidenceOffset: number | null;
  detail: string | null;
  naReason: string | null;
  isDeterministic: boolean;
  aiConfidence: number | null;
}

interface HygieneRun {
  id: string;
  score: number;
  passedCount: number;
  failedCount: number;
  reviewCount: number;
  naCount: number;
  ruleSetVersion: string;
  createdAt: string;
  checks: HygieneCheck[];
}

const RESULT_STYLE: Record<HygieneResult, { icon: typeof CheckCircleIcon; cls: string; label: string }> = {
  PASS: { icon: CheckCircleIcon, cls: 'text-emerald-600', label: 'Pass' },
  FAIL: { icon: XCircleIcon, cls: 'text-red-600', label: 'Not met' },
  NEEDS_REVIEW: { icon: ExclamationTriangleIcon, cls: 'text-amber-600', label: 'Needs review' },
  NOT_APPLICABLE: { icon: MinusCircleIcon, cls: 'text-gray-400', label: 'Not applicable' },
};

function scoreTone(score: number): string {
  if (score >= 85) return 'text-emerald-700';
  if (score >= 60) return 'text-amber-700';
  return 'text-red-700';
}

export default function PolicyHygienePanel({
  teamId,
  policyId,
}: {
  teamId: string;
  policyId: string;
}) {
  const queryClient = useQueryClient();
  const [runId, setRunId] = useState<string | null>(null);

  // Latest run for this policy, so the panel isn't empty on revisit.
  const { data: latestData } = useQuery({
    queryKey: ['policy-hygiene-latest', teamId],
    queryFn: async () => (await policyApi.hygiene.latest(teamId)).data,
    enabled: !!teamId,
  });

  const latestForPolicy = (latestData?.runs as { id: string; policyId: string }[] | undefined)
    ?.find((r) => r.policyId === policyId);
  const activeRunId = runId ?? latestForPolicy?.id ?? null;

  const { data: run, isLoading } = useQuery({
    queryKey: ['policy-hygiene-run', teamId, activeRunId],
    queryFn: async () => (await policyApi.hygiene.get(teamId, activeRunId!)).data as HygieneRun,
    enabled: !!activeRunId,
  });

  const runMutation = useMutation({
    mutationFn: async () => (await policyApi.hygiene.run(teamId, policyId)).data as HygieneRun,
    onSuccess: (data) => {
      setRunId(data.id);
      queryClient.invalidateQueries({ queryKey: ['policy-hygiene-latest', teamId] });
      queryClient.setQueryData(['policy-hygiene-run', teamId, data.id], data);
    },
  });

  return (
    <div className="bg-white border border-gray-200 rounded-lg">
      <div className="px-5 py-4 border-b border-gray-200 flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Policy hygiene</h3>
          <p className="mt-0.5 text-xs text-gray-500">
            Fourteen administrative requirements every policy should meet, regardless of subject.
          </p>
        </div>
        <button
          type="button"
          onClick={() => runMutation.mutate()}
          disabled={runMutation.isPending}
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800 disabled:opacity-50"
        >
          {runMutation.isPending ? 'Checking…' : run ? 'Re-check' : 'Check policy'}
        </button>
      </div>

      {runMutation.isError && (
        <div className="px-5 py-3 text-sm text-red-700 bg-red-50 border-b border-red-100">
          Couldn’t complete the check. Try again, or contact support if it persists.
        </div>
      )}

      {!activeRunId && !runMutation.isPending && (
        <div className="px-5 py-8 text-center">
          <p className="text-sm text-gray-500">This policy hasn’t been checked yet.</p>
        </div>
      )}

      {isLoading && activeRunId && (
        <div className="px-5 py-8 text-center text-sm text-gray-500">Loading…</div>
      )}

      {run && (
        <>
          <div className="px-5 py-4 border-b border-gray-100 flex flex-wrap items-baseline gap-x-6 gap-y-2">
            <div className="flex items-baseline gap-2">
              <span className={`text-3xl font-semibold tabular-nums ${scoreTone(run.score)}`}>
                {run.score}
              </span>
              <span className="text-sm text-gray-500">/ 100</span>
            </div>
            <div className="text-xs text-gray-600 space-x-3">
              <span>{run.passedCount} met</span>
              <span>{run.failedCount} not met</span>
              {run.reviewCount > 0 && <span className="text-amber-700">{run.reviewCount} need review</span>}
              {run.naCount > 0 && <span className="text-gray-400">{run.naCount} n/a</span>}
            </div>
            <div className="ml-auto text-[11px] text-gray-400">
              {run.ruleSetVersion} · {new Date(run.createdAt).toLocaleString()}
            </div>
          </div>

          <ul className="divide-y divide-gray-100">
            {run.checks.map((c) => {
              const style = RESULT_STYLE[c.result];
              const Icon = style.icon;
              return (
                <li key={c.id} className="px-5 py-3.5">
                  <div className="flex gap-3">
                    <Icon className={`h-5 w-5 shrink-0 mt-0.5 ${style.cls}`} aria-hidden />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="text-sm text-gray-900">{c.ruleText}</span>
                        <span className={`text-xs font-medium ${style.cls}`}>{style.label}</span>
                      </div>

                      {/* Provenance — a compliance officer must be able to tell a
                          database fact from a model's reading at a glance. */}
                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500">
                        {c.isDeterministic ? (
                          <span className="inline-flex items-center gap-1">
                            <CircleStackIcon className="h-3 w-3" aria-hidden />
                            From your policy record
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-violet-700">
                            <SparklesIcon className="h-3 w-3" aria-hidden />
                            AI-read from the document
                            {c.aiConfidence !== null && ` · ${Math.round(c.aiConfidence * 100)}% confidence`}
                          </span>
                        )}
                        {c.evidenceOffset !== null && <span>character {c.evidenceOffset.toLocaleString()}</span>}
                      </div>

                      {c.detail && <p className="mt-1 text-xs text-gray-600">{c.detail}</p>}

                      {c.naReason && (
                        <p className="mt-1 text-xs text-gray-500 italic">{c.naReason}</p>
                      )}

                      {/* The verbatim excerpt. This is the artifact that makes an
                          AI-derived pass defensible — quoted, not paraphrased. */}
                      {c.evidenceExcerpt && (
                        <blockquote className="mt-2 border-l-2 border-gray-200 pl-3 text-xs text-gray-700 italic">
                          “{c.evidenceExcerpt}”
                        </blockquote>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>

          <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 text-[11px] text-gray-500">
            Items marked <span className="text-amber-700 font-medium">needs review</span> were located in the
            document but couldn’t be quoted with enough confidence to pass automatically. They are not
            findings — a person should read the policy and decide.
          </div>
        </>
      )}
    </div>
  );
}
